import type { ZhKey } from './zh';

export const en: Record<ZhKey, string> = {
  // Tabs
  'tab.vps': 'Instances',
  'tab.billing': 'Billing',
  'tab.notifications': 'Inbox',
  'tab.tickets': 'Tickets',
  'tab.me': 'Account',

  // Common
  'common.back': 'Back',
  'common.save': 'Save',
  'common.delete': 'Delete',
  'common.cancel': 'Cancel',
  'common.confirm': 'Confirm',
  'common.loading': 'Loading…',
  'common.connecting': 'Connecting…',
  'common.empty': 'No data',
  'common.error': 'Error',
  'common.unknown': 'Unknown',
  'common.live': 'LIVE',
  'common.offline': 'OFFLINE',
  'common.dash': '—',
  'common.copy': 'Copy',
  'common.copied': 'Copied',
  'common.connectError': 'Connection error',
  'common.justNow': 'just now',
  'common.minutesAgo': '{n}m ago',
  'common.hoursAgo': '{n}h ago',
  'common.daysAgo': '{n}d ago',

  // Outside Telegram guard
  'guard.title': 'Open in Telegram',
  'guard.desc': "This is a Telegram Mini App. You'll need to open it from @neburst_dev_bot inside the Telegram client.",
  'guard.howTitle': 'How to open',
  'guard.step1': 'Search @neburst_dev_bot in Telegram',
  'guard.step2': 'Send /start — the bot replies with a button',
  'guard.step3': 'Tap "Open console" to enter the app',
  'guard.openBot': 'Open @neburst_dev_bot',

  // Login
  'login.title': 'Sign in to Neburst',
  'login.subtitle': 'Manage instances, billing, and tickets',
  'login.emailPlaceholder': 'Email',
  'login.passwordPlaceholder': 'Password',
  'login.submit': 'Sign in',
  'login.footer.agree': 'By signing in, you agree to the',
  'login.footer.tos': 'Neburst Terms of Service',
  'login.failed': 'Sign-in failed',

  // TOTP
  'totp.title': 'Two-step verification',
  'totp.subtitle': 'Enter the 6-digit code from your authenticator app',
  'totp.placeholder': '••••••',
  'totp.submit': 'Verify',
  'totp.backToLogin': 'Back to sign in',
  'totp.failed': 'Verification failed',
  'totp.footer': 'For passkey accounts, complete verification at neburst.com',

  // Status
  'status.running': 'Running',
  'status.stopped': 'Stopped',
  'status.starting': 'Starting',
  'status.stopping': 'Stopping',
  'status.rebooting': 'Rebooting',
  'status.creating': 'Creating',
  'status.deleting': 'Deleting',
  'status.error': 'Error',
  'status.unknown': 'Unknown',

  // VPS list
  'vps.list.title': 'My Instances',
  'vps.list.subtitleCount': '{total} total · {running} running',
  'vps.list.empty.title': 'No instances',
  'vps.list.empty.desc': "Your account doesn't have any instance yet",

  // VPS detail
  'vps.detail.network': 'Network',
  'vps.detail.realtime': 'Live metrics',
  'vps.detail.realtimeFooter': 'Refreshes every 15s (cached upstream)',
  'vps.detail.cpu': 'CPU',
  'vps.detail.memory': 'Memory',
  'vps.detail.in': 'Inbound',
  'vps.detail.out': 'Outbound',
  'vps.detail.traffic': 'Traffic this month',
  'vps.detail.power': 'Power',
  'vps.detail.power.start': 'Start',
  'vps.detail.power.restart': 'Restart',
  'vps.detail.power.stop': 'Stop',
  'vps.detail.confirmStop': 'Confirm shutdown?',
  'vps.detail.confirmForceStop': 'Force shutdown? Data may be lost',
  'vps.detail.history': 'Trend',
  'vps.detail.historySoon': 'Coming soon',
  'vps.detail.historyFooter': 'Phase 2: charts + work-request progress',

  'chart.title': 'Resource charts',
  'chart.network': 'Network',
  'chart.bandwidth': 'Traffic',
  'chart.footer': 'Last 6 hours (bandwidth is daily total in GB)',
  'chart.specs': 'Specs',
  'chart.storage': 'Storage',

  // Billing
  'billing.title': 'Billing',
  'billing.subtitle': 'Balance · Invoices · Records',
  'billing.balanceLabel': 'Account balance',
  'billing.frozen': 'Frozen',
  'billing.recentInvoices': 'Recent invoices',
  'billing.empty.title': 'No invoices yet',
  'billing.empty.desc': 'Top-ups and service charges will appear here',
  'billing.tools': 'Tools',
  'billing.balanceRecords': 'Balance records',
  'billing.balanceRecordsDesc': 'View top-ups and charges',
  'billing.payment': 'Payment methods / Auto-recharge',
  'billing.paymentDesc': 'Configure on desktop for sensitive actions',
  'billing.unknownInvoice': 'Invoice',
  'billing.topup': 'Top-up',
  'billing.charge': 'Charge',
  'billing.showAll': 'Show all {n}',

  // Invoice detail
  'invoice.detail.title': 'Invoice',
  'invoice.overview': 'Overview',
  'invoice.tradeNo': 'Invoice no.',
  'invoice.uuid': 'UUID',
  'invoice.status': 'Status',
  'invoice.type': 'Type',
  'invoice.amount': 'Amount',
  'invoice.applied': 'Applied balance',
  'invoice.discount': 'Discount',
  'invoice.times': 'Timeline',
  'invoice.createdAt': 'Created',
  'invoice.expiresAt': 'Expires',
  'invoice.paidAt': 'Paid',
  'invoice.footer': 'Payment/cancel actions are available on the desktop web at neburst.com.',

  // Notifications
  'notif.title': 'Inbox',
  'notif.countSuffix': '{n} items',
  'notif.markAllRead': 'Mark all read',
  'notif.empty.title': 'Inbox empty',
  'notif.empty.desc': 'New system messages will appear here',
  'notif.fallbackTitle': 'Notification',

  // Tickets
  'tickets.title': 'Tickets',
  'tickets.countSuffix': '{n} tickets',
  'tickets.new': 'New',
  'tickets.empty.title': 'No tickets',
  'tickets.empty.desc': 'Have an issue? Tap "New" to open one',
  'tickets.groupOpen': 'Open',
  'tickets.groupClosed': 'Closed',
  'tickets.replies': '{n} replies',
  'tickets.statusPending': 'Pending',
  'tickets.statusReplied': 'Replied',
  'tickets.statusClosed': 'Closed',

  // Ticket detail
  'ticket.detail.fallbackTitle': 'Ticket',
  'ticket.messages': 'Messages',
  'ticket.noMessages': 'No messages yet',
  'ticket.from.staff': 'Support',
  'ticket.from.ai': 'AI Assistant',
  'ticket.from.you': 'You',
  'ticket.reply': 'Reply',
  'ticket.replyPlaceholder': 'Type your reply',
  'ticket.replySubmit': 'Send reply',
  'ticket.dept.ai_assistant': 'AI Assistant',
  'ticket.dept.compute-instance': 'Compute · Instance',
  'ticket.dept.compute_instance': 'Compute · Instance',
  'ticket.dept.billing': 'Billing',
  'ticket.dept.account': 'Account',
  'ticket.dept.network': 'Network',
  'ticket.dept.general': 'General',
  'ticket.action.create_ticket': 'Ticket created',
  'ticket.action.close_ticket': 'Ticket closed',
  'ticket.action.reopen_ticket': 'Reopened',
  'ticket.action.mark_processing': 'Marked in progress',
  'ticket.action.reply_ticket': 'Replied',

  // New ticket
  'ticket.new.title': 'New ticket',
  'ticket.new.subtitle': 'Be detailed to speed up resolution',
  'ticket.new.titlePlaceholder': 'Title (short summary)',
  'ticket.new.contentPlaceholder': 'Details: errors, repro steps, affected instances, etc.',
  'ticket.new.submit': 'Submit',

  // Profile
  'profile.title': 'Account',
  'profile.subtitleLogged': 'Signed in',
  'profile.nicknameEmpty': 'No nickname set',
  'profile.editNickname': 'Edit nickname',
  'profile.alerts': 'Alert rules',
  'profile.alertsDesc': 'Push to Telegram when thresholds hit',
  'profile.language': 'Language',
  'profile.affiliate': 'Referrals',
  'profile.myInvites': 'My invites',
  'profile.invitesSummary': '{count} invited · {commission} total commission',
  'profile.about': 'About',
  'profile.aboutFooter': 'For password / 2FA / Passkey changes, use the desktop web at neburst.com.',
  'profile.version': 'Version',
  'profile.backend': 'Backend',
  'profile.logout': 'Sign out',
  'profile.confirmLogout': 'Sign out? Alert monitoring will stop.',

  // Alerts
  'alerts.title': 'Alert rules',
  'alerts.subtitle': 'Pushed via bot when thresholds hit',
  'alerts.configured': 'Configured',
  'alerts.empty.title': 'No rules yet',
  'alerts.empty.desc': 'Set a traffic threshold to get Telegram pushes',
  'alerts.add': 'New rule',
  'alerts.addFooter': 'Rules currently apply to all instances; per-instance scope coming soon',
  'alerts.threshold': 'Threshold',
  'alerts.addSubmit': 'Add rule',
  'alerts.cooldown': '{n}min cooldown',
  'alerts.scopeAll': 'All instances',
  'alerts.scopeOne': 'Single instance',
  'alerts.confirmDelete': 'Delete this rule?',

  // Invoice category
  'invoice.cat.compute': 'Compute',
  'invoice.cat.topup': 'Top-up',
  'invoice.cat.service': 'Service',
  'invoice.cat.network': 'Network',
  'invoice.cat.storage': 'Storage',
  'invoice.cat.refund': 'Refund',

  // Invoice status
  'invoice.status.paid': 'Paid',
  'invoice.status.unpaid': 'Unpaid',
  'invoice.status.pending': 'Pending',
  'invoice.status.partiallyPaid': 'Partially paid',
  'invoice.status.cancelled': 'Cancelled',
  'invoice.status.failed': 'Failed',
  'invoice.status.expired': 'Expired',

  // Pay cycle / plan
  'cycle.monthly': 'Monthly',
  'cycle.quarterly': 'Quarterly',
  'cycle.semiAnnually': 'Semi-annual',
  'cycle.annually': 'Annual',
  'cycle.onDemand': 'On-demand',
  'cycle.hourly': 'Hourly',

  // Instance list
  'vps.list.trafficUsed': '{pct}% used',

  // Realtime metrics
  'vps.detail.linkSpeed': 'Link speed',
  'vps.detail.liveRate': 'Current rate',
  'vps.detail.monthlyTraffic': 'Traffic plan',
  'vps.detail.updatedAgo': 'Updated {ago}',
  'vps.detail.refresh': 'Refresh',
  'vps.detail.trafficIn': 'In',
  'vps.detail.trafficOut': 'Out',
  'vps.detail.uptime': 'Uptime',
  'vps.detail.createdAt': 'Created',
  'vps.detail.expiresAt': 'Expires',
  'vps.detail.timesTitle': 'Timeline',
  'chart.live': 'Bandwidth',
  'chart.liveBandwidth': 'Live bandwidth',

  'metric.traffic_pct': 'Traffic %',
  'metric.traffic_gb_used': 'Traffic GB',
  'metric.instance_down': 'Down',
  'metric.expires_in_days': 'Expires',
  'metric.full.traffic_pct': 'Traffic percentage',
  'metric.full.traffic_gb_used': 'Traffic used (GB)',
  'metric.full.instance_down': 'Instance down',
  'metric.full.expires_in_days': 'Days to expiry',
};
